import { mapMaterialsToWriteMaterials } from '#api/adapters/materialAdapter.ts';
import type { ApiWriteOrder as ApiWriteOrderV2, ApiWriteStop as ApiWriteStopV2 } from '#api/model/types.ts';
import type {
    Location as FormDataLocation,
    MaterialFormData,
    OrderFormData,
    StopFormData,
} from '#components/order/CreateOrderDialog/OrderFormValidation.ts';
import { isCargoPackagingType } from '#components/order/CreateOrderDialog/packagingOptions.tsx';
import { composeDateTimeValue } from '#components/order/CreateOrderDialog/timeWindowFields.ts';
import type { Material } from '#domain/Material.ts';
import { isTransportOrder, type OrderStatus, type OrderSummary } from '#domain/Order.ts';
import type { ImportedOrder } from '#features/csvImport/ImportOrderFormValidation.ts';

import { parseGeoCoordinateString } from './geoParsing.util.ts';

const OrderStatusWeight: Record<NonNullable<OrderStatus>, number> = {
    open: 1,
    planned: 2,
    'in-transit': 3,
    loading: 4,
    unloading: 5,
    executing: 5,
    finished: 6,
};

export const formatOrderSidebarName = (order?: OrderSummary): string | undefined => {
    if (order && !isTransportOrder(order)) {
        return `"${order.number}"`;
    }
    if (order?.customerName && order?.number) {
        return `"${order.number} / ${order.customerName}"`;
    }
};

export const mapOrderStatusToOrderStatusWeight = (status: OrderStatus | undefined): number => {
    return status ? OrderStatusWeight[status] : 7;
};

export type OrderWithSortableKey<T extends OrderSummary = OrderSummary> = T & {
    statusWeight: number;
    loadingAddressEndDate?: string;
    unloadingAddressEndDate?: string;
    truckName?: string;
};

const mapWriteLocationV2 = (stopLocation: FormDataLocation): ApiWriteStopV2['location'] => ({
    street: stopLocation.street.trim(),
    city: stopLocation.city,
    zip_code: stopLocation.zipCode,
    country: stopLocation.country,
});

const mapWriteStopV2 = (stop: StopFormData): ApiWriteStopV2 => ({
    name: stop.name,
    location: mapWriteLocationV2(stop.location),
    start_target: composeDateTimeValue(stop.startDate, stop.startTime),
    end_target: composeDateTimeValue(stop.endDate, stop.endTime),
    type: stop.type === 'load' || stop.type === 'unload' ? stop.type : 'load',
    coordinates: mapCoordinates(stop),
});

const mapCoordinates = (stop: StopFormData) => {
    if (stop.latitude !== undefined && stop.longitude !== undefined) {
        return {
            latitude: parseGeoCoordinateString(stop.latitude),
            longitude: parseGeoCoordinateString(stop.longitude),
        };
    }

    if (stop.location.latitude !== undefined && stop.location.longitude !== undefined) {
        return {
            latitude: parseGeoCoordinateString(stop.location.latitude),
            longitude: parseGeoCoordinateString(stop.location.longitude),
        };
    }
    return undefined;
};

const mapFormMaterialToDomain = (material: MaterialFormData, index: number): Material => {
    if (material.type === 'cargo-material') {
        const packageType = isCargoPackagingType(material.packageType) ? material.packageType : 'unspecified';
        return {
            id: material.id ?? '',
            type: 'cargo-material',
            name: material.name,
            packageType,
            index: material.index ?? index,
            quantity: { target: material.quantity },
            weight: material.weight !== undefined ? { target: material.weight } : undefined,
            loadingMeter: material.loadingMeter !== undefined ? { target: material.loadingMeter } : undefined,
            length: material.length,
            width: material.width,
            height: material.height,
        };
    }

    return {
        id: material.id ?? '',
        type: 'bulk-material',
        packageType: 'bulk-material',
        name: material.name,
        unitType: material.unitType,
        quantity: { target: material.quantity },
        index: material.index ?? index,
    };
};

export const mapWriteOrder = (order: OrderFormData | ImportedOrder): ApiWriteOrderV2 => ({
    type: 'transport',
    number: order.number,
    customer_name: order.customerName,
    comments:
        order.loadingComment || order.unloadingComment
            ? {
                  loading: order.loadingComment,
                  unloading: order.unloadingComment,
              }
            : undefined,
    loading_stop: mapWriteStopV2(order.loadingStop),
    unloading_stop: mapWriteStopV2(order.unloadingStop),
    materials: mapMaterialsToWriteMaterials(order.materials.map(mapFormMaterialToDomain)),
});

export const addSortableOrderKey = <T extends OrderSummary>(items: T[]): OrderWithSortableKey<T>[] =>
    items.map(item => ({
        ...item,
        loadingAddressEndDate: isTransportOrder(item) ? item.loadingStop.endTarget : item.executionStop.endTarget,
        unloadingAddressEndDate: isTransportOrder(item) ? item.unloadingStop.endTarget : item.executionStop.endTarget,
        truckName: item.truck?.name,
        statusWeight: mapOrderStatusToOrderStatusWeight(item.status),
    }));
